import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import MenuClose from 'assets/icons/MenuClose.png'
import AbstractAuditLogDataGrid from 'common/AbstractAuditLogDataGrid'
import { RoundedButton } from 'common/Button/Button'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import { format } from 'date-fns'
import { useFormik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as yup from 'yup'

import {
  AllCustomerPayments,
  FinanceTableRows,
  GetCustomerPaymentTransaction,
  UpdateCustomerPayment
} from '../../../../../types/api/faber/payment.type'

import FaberConfirmCustomerUpdateDialog, {
  PaymentUpdatedValues
} from './FaberConfirmPaymentUpdateDialog'
interface PaymentStatusDialogProps {
  handlePaymentStatusDialogClose: (value: void) => void
  paymentStatusDialog: boolean
  handleMenuClose: (value: void) => void
  handleMarkAsPaid: (sin: string) => void
  paymentRow: AllCustomerPayments
  handleSubmit
}

const validationSchema = yup.object({
  paymentID: yup.string().required('Please fill in paymentID'),
  remarks: yup.string().required('Remarks are required')
})

const FaberFinanceUpdateDialog = ({
  handlePaymentStatusDialogClose,
  paymentStatusDialog,
  handleMenuClose,
  paymentRow
}: PaymentStatusDialogProps) => {
  function onCancelClick() {
    setConfirmUpdateDialogOpen(false)
  }
  const faberToken = OrbitSession.get('faberToken')

  const [initialValues] = useState<UpdateCustomerPayment>({
    orderID: paymentRow.orderID,
    customerID: paymentRow.customerID,
    paymentStatus: paymentRow.paymentStatus,
    paymentID: '',
    remarks: ''
  })
  const [updatedValues, setUpdatedValues] = useState<PaymentUpdatedValues>()
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] =
    useState<boolean>(false)
  const [reqBody, setReqBody] = useState<UpdateCustomerPayment>()

  // states for pagination and rows
  // const [pageOffset, setPageOffset] = useState(0)
  const [pageSize] = useState(5)
  // const [setRowCount] = useState(0)
  const [sortOrder] = useState('desc')
  const [rows, setRows] = useState<GetCustomerPaymentTransaction[]>([])

  //memoized query options
  const queryCustomerTxOptions = useMemo(() => {
    return {
      userId: paymentRow.customerID,
      // pageOffset,
      pageSize,
      sortOrder
    }
  }, [
    paymentRow.customerID,
    //  pageOffset,
    pageSize,
    sortOrder
  ])

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const tmpReqBody: UpdateCustomerPayment = {
        orderID: values.orderID,
        customerID: values.customerID,
        paymentStatus: values.paymentStatus,
        paymentID: values.paymentID,
        remarks: values.remarks
      }
      const updatedValues: PaymentUpdatedValues = {
        paymentStatus: values.paymentStatus,
        paymentID: values.paymentID,
        remarks: values.remarks
      }
      setReqBody(tmpReqBody)
      setUpdatedValues(updatedValues)
      setConfirmUpdateDialogOpen(true)
    }
  })

  const { mutate: updatePaymentDetails } = useMutation(
    PaymentAPI.updatePaymentSuccess,
    {
      onSuccess: () => {
        toast.success('Payment details updated')
      },
      onError: () => {
        toast.error('Error updating payment details')
      }
    }
  )

  const confirmChanges = () => {
    if (updatedValues && reqBody) {
      updatePaymentDetails({ ...{ faberToken: faberToken }, ...reqBody })
    }
    setConfirmUpdateDialogOpen(false)
    handlePaymentStatusDialogClose()
    handleMenuClose()
  }

  const {
    data: customerTxData
    // isLoading: isCustomerTransactionLoading
  } = useQuery(
    ['getIndividualCustomerTransaction', [queryCustomerTxOptions]],
    async () => {
      return PaymentAPI.getCustomerTransactions({
        customerId: paymentRow.customerID,
        // offset: pageOffset,
        limit: pageSize,
        order: sortOrder
      })
    }
  )

  useEffect(() => {
    if (customerTxData) {
      const { data: txData } = customerTxData.data.body
      setRows(txData)
      // setRowCount(5)
    }
  }, [customerTxData])

  // const handlePageChange = (newPage: number) => {
  //   const tmpOffset = newPage * pageSize
  //   setPageOffset(tmpOffset)
  // }

  const dialogClose = () => {
    handlePaymentStatusDialogClose()
    handleMenuClose()
  }

  const columns: GridColDef[] = [
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.5,
      minWidth: 30,
      filterable: false
    },
    {
      field: 'trn',
      headerName: 'Transaction Reference Number',
      flex: 1.5,
      minWidth: 100,
      filterable: false
    },
    {
      field: 'paymentType',
      headerName: 'Payment Type',
      flex: 1,
      minWidth: 100,
      filterable: false
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      flex: 1,
      minWidth: 100,
      filterable: false
    },
    {
      field: 'updated',
      headerName: 'Updated Date',
      flex: 1,
      minWidth: 100,
      filterable: false
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      flex: 1,
      minWidth: 100,
      filterable: false
    }
  ]

  return (
    <Dialog
      open={paymentStatusDialog}
      onClose={dialogClose}
      fullWidth={true}
      maxWidth='md'
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: '-10px',
              mt: '-10px'
            }}
          >
            <Typography
              sx={{ color: 'black', fontSize: '18px', fontWeight: '700' }}
            >
              Payment Status
            </Typography>
            <IconButton
              onClick={dialogClose}
              size='small'
              data-cy='closeDialogBtn'
              sx={{
                mr: '-10px',
                mt: '-10px',
                width: '40px',
                height: '40px'
              }}
            >
              <img
                style={{
                  width: '20px',
                  height: 'auto'
                }}
                src={MenuClose}
              />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider sx={{ width: '100%' }} />
        <DialogContent>
          <DialogContentText sx={{ marginBottom: '24px', marginLeft: '0' }}>
            {/* TO DO: INTEGRATE WITH BACKEND */}
            <TextColorFontBody sx={{ fontSize: '16px', fontWeight: 500 }}>
              Total amount due{' '}
              {paymentRow.paymentStatus === 'SUCCESS'
                ? '$0'
                : '$' + paymentRow.totalDue}
            </TextColorFontBody>
          </DialogContentText>
          <Box
            component='form'
            sx={{
              // '& .MuiTextField-root': { m: 1, width: '25%' },
              rowGap: '1em',
              columnGap: '1em',
              gridTemplateColumns: 'repeat(3, 1fr)',
              mb: '-10px'
            }}
            display='grid'
            noValidate
            autoComplete='off'
          >
            <TextField
              id='Transaction Reference Number'
              label='Transaction Reference Number'
              defaultValue={paymentRow.orderID}
              variant='standard'
              disabled
              sx={{
                '&& .MuiInput-input.Mui-disabled': {
                  '-webkit-text-fill-color': 'rgba(0,0,0,0.87)'
                },
                '&& .MuiInputBase-root.Mui-disabled:before': {
                  'border-bottom-style': 'none'
                },
                '&& .MuiInputLabel-root.Mui-disabled': {
                  color: '#5a5d5f'
                }
              }}
            />
            <TextField
              id='Payment Type'
              label='Payment Type'
              defaultValue={paymentRow.paymentType}
              variant='standard'
              disabled
              sx={{
                '&& .MuiInput-input.Mui-disabled': {
                  '-webkit-text-fill-color': 'rgba(0,0,0,0.87)'
                },
                '&& .MuiInputBase-root.Mui-disabled:before': {
                  'border-bottom-style': 'none'
                },
                '&& .MuiInputLabel-root.Mui-disabled': {
                  color: '#5a5d5f'
                }
              }}
            />
            <TextField
              id='Date'
              label='Date'
              defaultValue={format(
                new Date(paymentRow.billStart),
                'dd MMM yyyy'
              )}
              variant='standard'
              disabled
              sx={{
                '&& .MuiInput-input.Mui-disabled': {
                  '-webkit-text-fill-color': 'rgba(0,0,0,0.87)'
                },
                '&& .MuiInputBase-root.Mui-disabled:before': {
                  'border-bottom-style': 'none'
                },
                '&& .MuiInputLabel-root.Mui-disabled': {
                  color: '#5a5d5f'
                }
              }}
            />
            <Autocomplete
              id='Payment Status'
              options={['SUCCESS']}
              value={formik.values.paymentStatus}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Payment Status'
                  variant='standard'
                />
              )}
              onChange={(e, v) => {
                formik.setFieldValue('paymentStatus', v)
              }}
            />
            <TextField
              variant='standard'
              label='Payment ID'
              id='paymentID'
              multiline
              value={formik.values.paymentID}
              onChange={formik.handleChange}
              error={
                formik.touched.paymentID && Boolean(formik.errors.paymentID)
              }
              helperText={formik.touched.paymentID && formik.errors.paymentID}
              sx={{ width: '100%' }}
            />
            <TextField
              variant='standard'
              label='Remarks'
              id='remarks'
              multiline
              value={formik.values.remarks}
              onChange={formik.handleChange}
              error={formik.touched.remarks && Boolean(formik.errors.remarks)}
              helperText={formik.touched.remarks && formik.errors.remarks}
              sx={{ width: '100%' }}
            />
          </Box>
        </DialogContent>
        <DialogContent sx={{ width: '100%', marginTop: '30px', padding: '0' }}>
          <Box
            sx={{
              // '& .MuiTextField-root': { m: 1, width: '25%' },
              rowGap: '0.5em',
              gridTemplateColumns: 'repeat(3, 15em)',
              backgroundColor: '#F9F9F9',
              width: '100%',
              paddingX: '24px'
            }}
            display='grid'
          >
            <Box>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '500',
                  color: '#253140',
                  fontSize: '14px'
                }}
              >
                Billing Period
              </Typography>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '400',
                  color: '#818D9C',
                  fontSize: '16px'
                }}
              >
                {format(new Date(paymentRow.billStart), 'dd MMM yyyy')} -{' '}
                {format(new Date(paymentRow.billEnd), 'dd MMM yyyy')}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '500',
                  color: '#253140',
                  fontSize: '14px'
                }}
              >
                Status
              </Typography>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '400',
                  color: '#818D9C',
                  fontSize: '16px'
                }}
              >
                {paymentRow.paymentStatus}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '500',
                  color: '#253140',
                  fontSize: '14px'
                }}
              >
                Total Incurred
              </Typography>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '400',
                  color: '#818D9C',
                  fontSize: '16px'
                }}
              >
                ${paymentRow.totalIncurred}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '500',
                  color: '#253140',
                  fontSize: '14px'
                }}
              >
                Total Paid
              </Typography>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '400',
                  color: '#818D9C',
                  fontSize: '16px'
                }}
              >
                ${paymentRow.totalPaid}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '500',
                  color: '#253140',
                  fontSize: '14px'
                }}
              >
                Total Due
              </Typography>
              <Typography
                sx={{
                  marginLeft: '0px',
                  fontWeight: '400',
                  color: '#818D9C',
                  fontSize: '16px'
                }}
              >
                ${paymentRow.totalDue}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogContent sx={{ mb: '-10px' }}>
          <DialogContentText sx={{ marginBottom: '10px', marginLeft: '0' }}>
            <TextColorFontBody
              sx={{ fontSize: '14px', fontWeight: 500, color: '#818D9C' }}
            >
              Payment Log
            </TextColorFontBody>
          </DialogContentText>
          <Box sx={{ height: '300px' }}>
            <AbstractAuditLogDataGrid
              getRowId={(row) =>
                row?.trn
                  ? row.trn + rows.indexOf(row).toString()
                  : rows.indexOf(row).toString() + 'financeRow'
              }
              rows={rows}
              columns={columns}
              tableHeight='300px'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <RoundedButton variant='filled' onClick={onCancelClick}>
            Cancel
          </RoundedButton>
          <RoundedButton variant='filled' type='submit'>
            Submit
          </RoundedButton>
        </DialogActions>
      </form>
      {updatedValues && (
        <FaberConfirmCustomerUpdateDialog
          handleDialogClose={() => {
            onCancelClick()
          }}
          confirmUpdateDialogOpen={confirmUpdateDialogOpen}
          initialValues={initialValues}
          values={updatedValues}
          confirmChanges={confirmChanges}
        />
      )}
      {/* </ColorBox> */}
    </Dialog>
  )
}

export interface PaymentLogProps {
  transactionRows: FinanceTableRows[]
}

export interface UpdatedRow {
  paymentStatus: string
}

export default FaberFinanceUpdateDialog
